import React, { useMemo } from 'react';
import { Input, Select, Typography, DatePicker } from 'antd';
// Constants
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_TITLE_LENGTH,
} from '../TimelineDeliverablesModal';
// Helpers
import {
  TimelineDeliverableFormData,
  convertToMoment,
  convertToDate,
  getContentDueDateTypeOptions,
  generateContentPieces,
  getDisabledDate,
  createNumberOptions,
  getApprovalUnitOptions,
} from 'helpers/timelineDeliverables';
// Types
import {
  CampaignMediaType,
  CbDueDateType,
  CbDisplayPeriod,
} from 'api/graphql-global-types';
// Components
import UploadFilesInput from 'ui/UploadFIlesInput/UploadFilesInput';
// Styles
import styles from './ContentSetup.module.scss';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

type ContentSetupProps = {
  formData: TimelineDeliverableFormData;
  updateNestedFormData: (path: string, value: any) => void;
  /**
   * Media type options to offer per content piece. When omitted, defaults to
   * Video + Image (unchanged legacy behaviour). Custom campaigns pass a list
   * restricted by the selected post type (e.g. Reel/Live -> Video only).
   */
  mediaTypeOptions?: { label: string; value: CampaignMediaType }[];
  showExampleFiles?: boolean;
};

const DEFAULT_MEDIA_TYPE_OPTIONS: {
  label: string;
  value: CampaignMediaType;
}[] = [
  { label: 'Video', value: CampaignMediaType.VIDEO },
  { label: 'Image', value: CampaignMediaType.IMAGE },
];

const ContentSetup: React.FC<ContentSetupProps> = ({
  formData,
  updateNestedFormData,
  mediaTypeOptions = DEFAULT_MEDIA_TYPE_OPTIONS,
  showExampleFiles = false,
}) => {
  const contentAmount = formData.contentAmount || 1;
  const isFixedContentDate =
    formData.contentSetup?.dueDateType === CbDueDateType.FIXED;
  const isApplicationBasedContentDate =
    formData.contentSetup?.dueDateType === CbDueDateType.APPLICATION_BASED;

  // Memoized options
  const contentDueDateTypeOptions = useMemo(
    () => getContentDueDateTypeOptions(),
    []
  );
  const approvalUnitOptions = useMemo(() => getApprovalUnitOptions(), []);
  const contentPieces = formData.contentPieces || [];

  // Event handlers
  const handleContentAmountChange = (value: number) => {
    updateNestedFormData('contentAmount', value);
    const newContentPieces = generateContentPieces(value, contentPieces);
    updateNestedFormData('contentPieces', newContentPieces);
  };

  const handleContentDueDateTypeChange = (option: any) => {
    updateNestedFormData('contentSetup.dueDateType', option);
  };

  const handleApprovalUnitChange = (option: any) => {
    updateNestedFormData('contentSetup.approvalUnit', option);
    updateNestedFormData('contentSetup.approvalNumber', undefined);
  };

  const handleApprovalNumberChange = (option: string) => {
    updateNestedFormData('contentSetup.approvalNumber', Number(option));
  };

  const handleContentPieceChange = (
    index: number,
    field: string,
    value: any
  ) => {
    const newContentPieces = [...contentPieces];
    newContentPieces[index] = {
      ...newContentPieces[index],
      [field]: value,
    };
    updateNestedFormData('contentPieces', newContentPieces);
  };

  return (
    <div className={styles.contentSetup}>
      <Title level={3}>Content Setup</Title>

      <div className={styles.helperText}>
        Set up the content requirements for this deliverable. Specify how many
        content pieces you need and when they should be delivered.
      </div>

      {/* Content Amount */}
      <div className={styles.formGroup}>
        <div>Number of Content Pieces</div>
        <Select
          value={contentAmount}
          onChange={handleContentAmountChange}
          placeholder="Select amount"
          style={{ width: '200px' }}
        >
          {createNumberOptions(10).map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </div>

      {/* Content Pieces */}
      <div className={styles.contentPieces}>
        <Title level={4}>Content Pieces</Title>
        <div className={styles.helperText}>
          Specify the details for each content piece you need.
        </div>

        {contentPieces.map((piece, index) => (
          <div key={index} className={styles.contentPiece}>
            <div className={styles.contentPieceHeader}>
              <Title level={5}>Content Piece {index + 1}</Title>
            </div>

            <div className={styles.formGroup}>
              <div>Media Type</div>
              <Select
                value={piece.mediaType}
                onChange={(value) =>
                  handleContentPieceChange(index, 'mediaType', value)
                }
                placeholder="Select media type"
                style={{ width: '200px' }}
              >
                {mediaTypeOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div className={styles.formGroup}>
              <div>Title</div>
              <Input
                value={piece.title}
                onChange={(e) =>
                  handleContentPieceChange(index, 'title', e.target.value)
                }
                placeholder="Enter title"
                maxLength={MAX_TITLE_LENGTH}
              />
            </div>

            <div className={styles.formGroup}>
              <div>Description</div>
              <TextArea
                value={piece.description}
                onChange={(e) =>
                  handleContentPieceChange(index, 'description', e.target.value)
                }
                rows={3}
                placeholder="Enter description"
                maxLength={MAX_DESCRIPTION_LENGTH}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Content Due Date Type */}
      <div className={styles.formGroup}>
        <div>Content Due Date</div>
        <Select
          value={formData.contentSetup?.dueDateType}
          onChange={handleContentDueDateTypeChange}
          placeholder="Select due date type"
          style={{ width: '100%' }}
        >
          {contentDueDateTypeOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </div>

      {/* Application-Based Content Timeline */}
      {isApplicationBasedContentDate && (
        <div className={styles.rowWrapper}>
          <div className={styles.formGroup}>
            <div>Unit</div>
            <Select
              value={formData.contentSetup?.approvalUnit}
              onChange={handleApprovalUnitChange}
              placeholder="Select unit"
              style={{ width: '200px' }}
            >
              {approvalUnitOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className={styles.formGroup}>
            <div>Number</div>
            <Select
              value={formData.contentSetup?.approvalNumber?.toString()}
              onChange={handleApprovalNumberChange}
              placeholder="Select number"
              style={{ width: '200px' }}
            >
              {createNumberOptions(
                formData.contentSetup?.approvalUnit === CbDisplayPeriod.DAYS
                  ? 6
                  : 4
              ).map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      )}

      {/* Fixed Content Date */}
      {isFixedContentDate && (
        <div className={styles.formGroup}>
          <div>Due Date</div>
          <DatePicker
            value={convertToMoment(formData.contentSetup?.dueDate)}
            onChange={(date) =>
              updateNestedFormData('contentSetup.dueDate', convertToDate(date))
            }
            placeholder="Select due date"
            style={{ width: '200px' }}
            disabledDate={getDisabledDate}
          />
        </div>
      )}

      {showExampleFiles && (
        <UploadFilesInput
          title="Deliverable Examples & Guidelines"
          value={formData.examples || []}
          onChange={(examples) => updateNestedFormData('examples', examples)}
          acceptFileTypes={[
            'image/png',
            'image/jpeg',
            'video/mp4',
            'video/quicktime',
            'application/pdf',
          ]}
          inputDescription="Upload brand guidelines, scripts, or examples for this deliverable."
        />
      )}
    </div>
  );
};

export default ContentSetup;
